import React, { useEffect, useRef } from 'react';
import { Link } from 'react-scroll';
import { FormattedMessage } from 'react-intl';
import { always, isEmpty } from 'ramda';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';

import { Artist, ArtworkDescription, Image, Artwork } from '@definitions/artworks.types';
import { renderWhenTrue } from '@utils/rendering';
import { KlarnaPlacement } from '@components/klarnaPlacement';
import { LinkArrow } from '@components/common';
import { SocialShare } from '@components/PDP/socialShare';
import { TalkToExpert } from '@components/PDP/talkToExpert';
import { OfferedIn } from '@components/offeredIn';
import { UnitCountDown } from '@components/unitCountDown';

import { SCROLL_DURATION, DETAILS_SCROLL_LINK, IMAGE_SCROLL_LINK } from '../pdp.constants';
import { ExpiringBlock } from '../expiringBlock';
import { KlarnaContainer } from '../artworkDetails/artworkDetails.styles';

import {
  Container,
  Description,
  BulletPoints,
  Bullet,
  DescriptionLink,
  LinkCopy,
  Thumbnail,
  MotionContainer,
  OfferedInDesktopContainer,
} from './descriptionBlock.styles';

export interface DescriptionBlockProps {
  description: ArtworkDescription;
  image: Image;
  title: string;
  artist: Artist;
  setDescriptionHeight: (number: number) => void;
  liveEndAt: string;
  imagesInView: boolean;
  isInStock: boolean;
  price: number;
  isPostSellingDate?: boolean;
  inventoryLevel: number;
  lowInventoryLevel: number;
  isWithinSellingDates?: boolean;
  category: string;
  subCategory: string;
}

export const DescriptionBlock = ({
  description,
  image,
  title,
  artist,
  setDescriptionHeight,
  liveEndAt,
  imagesInView,
  isInStock,
  price,
  isPostSellingDate,
  inventoryLevel,
  lowInventoryLevel,
  isWithinSellingDates,
  category,
  subCategory,
}: DescriptionBlockProps) => {
  const router = useRouter();
  const imageRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const transition = { ease: 'easeInOut', duration: 0.6 };
  const isBeforeOneMonth = dayjs(liveEndAt).isBefore(dayjs().utc().add(30, 'day'));
  const isExclusive = router.pathname === '/exclusive/[slug]';

  useEffect(() => {
    const element = imagesInView ? contentRef : imageRef;
    setTimeout(() => {
      setDescriptionHeight(element.current?.offsetHeight || 0);
    });
  }, [imagesInView]);

  const renderDescription = renderWhenTrue(
    always(
      <Description>
        {description.shortDescription}
        <DescriptionLink>
          <LinkArrow />
          <LinkCopy>
            <FormattedMessage id="descriptionBlock.link" defaultMessage="More" />
          </LinkCopy>
        </DescriptionLink>
      </Description>
    )
  );

  const renderExpiringBlock = renderWhenTrue(() => <ExpiringBlock liveEndAt={liveEndAt} />);

  const renderWhenOffered = renderWhenTrue(() => (
    <OfferedInDesktopContainer>
      <OfferedIn liveEndAt={liveEndAt} />
    </OfferedInDesktopContainer>
  ));

  const renderUnitCountDown = renderWhenTrue(() => <UnitCountDown withIcon inventoryLevel={inventoryLevel} />);

  return (
    <Container>
      <MotionContainer
        initial={{ opacity: imagesInView ? 0 : 1, y: -20 }}
        animate={{ opacity: imagesInView ? 0 : 1, y: imagesInView ? -20 : 0 }}
        transition={transition}
        ref={imageRef}
      >
        <Link to={IMAGE_SCROLL_LINK} href={`#${IMAGE_SCROLL_LINK}`} smooth spy duration={SCROLL_DURATION}>
          <Thumbnail src={image?.urlTiny} alt={image?.description} />
        </Link>
      </MotionContainer>
      <MotionContainer
        initial={{ opacity: imagesInView ? 1 : 0, y: 0 }}
        animate={{ opacity: imagesInView ? 1 : 0, y: imagesInView ? 0 : -20 }}
        transition={transition}
        ref={contentRef}
      >
        <KlarnaContainer style={{ marginTop: -10 }}>
          <KlarnaPlacement typeOfDataKey="top-strip-promotion-auto-size" price={price} />
        </KlarnaContainer>
        <Link to={DETAILS_SCROLL_LINK} href={`#${DETAILS_SCROLL_LINK}`} smooth spy duration={SCROLL_DURATION}>
          {renderDescription(!isEmpty(description.shortDescription))}
        </Link>
        <BulletPoints>
          {description.bulletPoints
            ?.map((bullet, index) => {
              if (
                (category.toLowerCase() === 'products' || subCategory.toLowerCase() === 'sculptures') &&
                (bullet.toLowerCase().includes('framed') || bullet.toLowerCase().includes('unframed'))
              ) {
                return null;
              }
              return <Bullet key={index}>{bullet}</Bullet>;
            })
            .filter(Boolean)}
        </BulletPoints>
        {renderExpiringBlock(isBeforeOneMonth && isInStock)}
        {renderUnitCountDown(inventoryLevel > 0 && isWithinSellingDates && inventoryLevel <= lowInventoryLevel)}
        {renderWhenOffered(isPostSellingDate && !isExclusive)}

        <SocialShare title={title} artist={artist} />
        <TalkToExpert title={title} artist={artist} />
      </MotionContainer>
    </Container>
  );
};
